import { useState, useMemo, useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import apiClient from '@/lib/api'
import { cn } from '@/lib/cn'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { Switch } from '@/components/ui/Switch'
import { StatsCard } from '@/components/monitoring/StatsCard'
import { MessageRow } from '@/components/monitoring/MessageRow'
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  InboxIcon,
} from '@heroicons/react/24/outline'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface HL7MessageLog {
  id: string
  tenantId: string
  messageControlId: string
  messageType: string
  triggerEvent: string
  sendingFacility: string
  receivedAt: string | number
  processingStatus: string
  processingOutcome: string
  errorMessage?: string
  rawMessageHash: string
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Tous les statuts' },
  { value: 'COMPLETED', label: 'Terminé' },
  { value: 'FAILED', label: 'Échec' },
  { value: 'PROCESSING', label: 'En cours' },
  { value: 'RECEIVED', label: 'Reçu' },
] as const

const EVENT_OPTIONS = [
  { value: 'ALL', label: 'Tous les événements' },
  { value: 'A01', label: 'A01 - Admission' },
  { value: 'A02', label: 'A02 - Transfert' },
  { value: 'A03', label: 'A03 - Sortie' },
  { value: 'A04', label: 'A04 - Inscription' },
  { value: 'A05', label: 'A05 - Pré-admission' },
  { value: 'A08', label: 'A08 - Mise à jour' },
  { value: 'A11', label: 'A11 - Annulation admission' },
  { value: 'A12', label: 'A12 - Annulation transfert' },
  { value: 'A13', label: 'A13 - Annulation sortie' },
] as const

const REFETCH_INTERVAL_MS = 10_000

/* ------------------------------------------------------------------ */
/*  Data fetching                                                      */
/* ------------------------------------------------------------------ */

async function fetchHL7Messages(): Promise<HL7MessageLog[]> {
  const response = await apiClient.get<HL7MessageLog[]>('/api/v1/admin/hl7/messages', {
    params: { limit: 50 },
  })
  return response.data
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                     */
/* ------------------------------------------------------------------ */

export default function MonitoringPage() {
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [eventFilter, setEventFilter] = useState('ALL')
  const [searchQuery, setSearchQuery] = useState('')

  const {
    data: messages,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['hl7-messages'],
    queryFn: fetchHL7Messages,
    staleTime: REFETCH_INTERVAL_MS,
    refetchInterval: autoRefresh ? REFETCH_INTERVAL_MS : false,
    meta: { errorMessage: 'Impossible de charger les messages HL7' },
  })

  // Show toast on error (useEffect to avoid firing on every render)
  useEffect(() => {
    if (isError) {
      toast.error('Erreur lors du chargement des messages HL7')
    }
  }, [isError])

  /* ---- Stats ---- */
  const stats = useMemo(() => {
    const list = messages ?? []
    return {
      total: list.length,
      completed: list.filter((m) => m.processingStatus === 'COMPLETED').length,
      failed: list.filter((m) => m.processingStatus === 'FAILED').length,
      processing: list.filter((m) => m.processingStatus === 'PROCESSING').length,
    }
  }, [messages])

  /* ---- Filtering (client-side) ---- */
  const filteredMessages = useMemo(() => {
    let result = messages ?? []

    if (statusFilter !== 'ALL') {
      result = result.filter((m) => m.processingStatus === statusFilter)
    }

    if (eventFilter !== 'ALL') {
      result = result.filter((m) => m.triggerEvent === eventFilter)
    }

    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (m) =>
          m.sendingFacility.toLowerCase().includes(q) ||
          m.messageControlId.toLowerCase().includes(q)
      )
    }

    return result
  }, [messages, statusFilter, eventFilter, searchQuery])

  const handleRefresh = useCallback(() => {
    void refetch()
  }, [refetch])

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 pb-8 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Monitoring HL7</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Derniers messages HL7v2 ingérés</p>
        </div>
        <div className="flex items-center gap-4">
          <Switch
            checked={autoRefresh}
            onChange={setAutoRefresh}
            label="Auto"
            size="sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            isLoading={isFetching}
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
          >
            Rafraîchir
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatsCard label="Total messages" value={stats.total} variant="default" />
        <StatsCard label="Terminés" value={stats.completed} variant="success" />
        <StatsCard label="Échoués" value={stats.failed} variant="error" />
        <StatsCard label="En cours" value={stats.processing} variant="warning" />
      </div>

      {/* Filter bar */}
      <Card variant="bordered" padding="sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 text-neutral-400">
            <FunnelIcon className="w-4 h-4 shrink-0" />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={cn(
              'block rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-700',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            )}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Event filter */}
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className={cn(
              'block rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-700',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            )}
          >
            {EVENT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Text search */}
          <div className="relative flex-1 min-w-0 w-full sm:w-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-neutral-400">
              <MagnifyingGlassIcon className="w-4 h-4" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par établissement ou ID..."
              className={cn(
                'block w-full rounded-md border border-neutral-300 bg-white pl-9 pr-3 py-1.5 text-sm text-neutral-700',
                'placeholder:text-neutral-400',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              )}
            />
          </div>
        </div>
      </Card>

      {/* Message list */}
      <Card variant="bordered" padding="none">
        {isLoading ? (
          /* Loading state */
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-neutral-500">Chargement des messages...</p>
          </div>
        ) : filteredMessages.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <InboxIcon className="w-10 h-10 text-neutral-300" />
            <p className="text-sm font-medium text-neutral-500">Aucun message trouvé</p>
            <p className="text-xs text-neutral-400">
              {(statusFilter !== 'ALL' || eventFilter !== 'ALL' || searchQuery.trim() !== '')
                ? 'Essayez de modifier vos filtres'
                : 'Les messages HL7v2 apparaîtront ici une fois ingérés'}
            </p>
          </div>
        ) : (
          /* Message rows */
          <div>
            {filteredMessages.map((msg) => (
              <MessageRow key={msg.id} message={msg} />
            ))}
          </div>
        )}
      </Card>

      {/* Footer count */}
      {!isLoading && filteredMessages.length > 0 && (
        <p className="text-xs text-neutral-400 text-center">
          {filteredMessages.length} message{filteredMessages.length > 1 ? 's' : ''} affiché{filteredMessages.length > 1 ? 's' : ''}
          {(statusFilter !== 'ALL' || eventFilter !== 'ALL' || searchQuery.trim() !== '')
            ? ` sur ${messages?.length ?? 0} au total`
            : ''}
        </p>
      )}
    </div>
  )
}
